import type { StringTable } from '../index.ts';

/** Handwriting pad + calibration strings (namespace "hw"). EN and AF must have identical keys. */
export const HANDWRITING: StringTable = {
  en: {
    // Pad
    padLabel: 'Handwriting pad: write your answer with your finger or a pen. You can also type it.',
    placeholder: 'Write your answer here',
    placeholderCompact: 'Write one digit',
    answerLine: 'Your answer',
    chipLabel: 'Character {n} of {count}: {char}. Tap to change it.',
    chipLabelUnsure: "Character {n} of {count}: {char}, but I'm not sure. Tap to change it.",
    typedLabel: 'Typed answer: {text}',
    submit: 'Go',
    submitLabel: 'Send my answer',
    undo: 'Undo',
    undoLabel: 'Undo the last stroke',
    clear: 'Clear',
    clearLabel: 'Clear the pad',
    choices: 'Choices',
    choicesLabel: 'Show the answer choices instead',
    hintUnsure: 'Tap a character to fix it.',
    msgEmpty: 'Write your answer on the pad first.',
    msgNotNumber: "That's not a number yet. Check what you typed.",
    msgUnreadable: "Hmm, that doesn't look like a number yet. Try writing it again, nice and big.",
    msgSuggest: 'Did you mean {answer}? Tap ✓ to send it, or tap a character to fix it.',
    msgFallback: "Let's pick from choices for this one!",
    charMinus: 'minus',
    charSlash: 'fraction line',
    charPoint: 'decimal point',

    // Calibration
    calTitle: 'Teach Dropstack your handwriting',
    calIntro:
      'Write each number and sign twice, so Dropstack learns how you write them. It takes about two minutes.',
    calTipsTitle: 'Tips',
    tipBig: 'Write big: fill most of the box.',
    tipOrder: 'Write answers from left to right.',
    tipFraction: 'Use a slanted line / for fractions, like 3/4.',
    tipDot: 'Make the decimal point a small, clear dot.',
    calStart: 'Start',
    calNotNow: 'Not now',
    calSavedCount: 'You have {count} saved handwriting samples.',
    calReset: 'Forget my handwriting',
    calResetDone: 'Done! Dropstack will use its standard shapes again.',
    calProgress: '{done} of {total}',
    calProgressLabel: 'How far you are',
    calPrompt: 'Write {char}',
    calRepeat: '({n} of {total})',
    hintMinus: 'A short, flat line (minus)',
    hintSlash: 'A slanted line, like in 3/4',
    hintPoint: 'A small dot',
    calPadLabel: 'Writing box for {char}',
    calBack: 'Back',
    calBackLabel: 'Go back and write the previous one again',
    calRedo: 'Redo',
    calRedoLabel: 'Clear the box and write it again',
    calSkip: 'Skip',
    calNext: 'Next',
    calSaveFinish: 'Save & finish',
    calDoneTitle: 'All done!',
    calDoneBody: 'Dropstack now knows your handwriting. Write a number to test it:',
    calTryLabel: 'Test box: write a number',
    calTryResult: 'I read: {text}',
    calTryEmpty: "Write something and I'll read it.",
    calTryClear: 'Clear',
    calFinish: 'Finish',
  },
  af: {
    // Skryfblok
    padLabel: "Skryfblok: skryf jou antwoord met jou vinger of ’n pen. Jy kan dit ook tik.",
    placeholder: 'Skryf jou antwoord hier',
    placeholderCompact: 'Skryf een syfer',
    answerLine: 'Jou antwoord',
    chipLabel: 'Simbool {n} van {count}: {char}. Tik om dit te verander.',
    chipLabelUnsure: 'Simbool {n} van {count}: {char}, maar ek is nie seker nie. Tik om dit te verander.',
    typedLabel: 'Getikte antwoord: {text}',
    submit: 'Stuur',
    submitLabel: 'Stuur my antwoord',
    undo: 'Ontdoen',
    undoLabel: 'Haal die laaste streep weg',
    clear: 'Vee uit',
    clearLabel: 'Vee die skryfblok skoon',
    choices: 'Keuses',
    choicesLabel: 'Wys eerder die antwoordkeuses',
    hintUnsure: "Tik op ’n simbool om dit reg te maak.",
    msgEmpty: 'Skryf eers jou antwoord op die skryfblok.',
    msgNotNumber: "Dis nog nie ’n getal nie. Kyk weer wat jy getik het.",
    msgUnreadable: "Hmm, dit lyk nog nie soos ’n getal nie. Probeer dit weer skryf, lekker groot.",
    msgSuggest: "Bedoel jy {answer}? Tik ✓ om dit te stuur, of tik op ’n simbool om dit reg te maak.",
    msgFallback: 'Kom ons kies hierdie een uit die keuses!',
    charMinus: 'minus',
    charSlash: 'breukstreep',
    charPoint: 'desimale komma',

    // Kalibrasie
    calTitle: 'Leer Dropstack om jou handskrif te lees',
    calIntro:
      'Skryf elke syfer en teken twee keer, sodat Dropstack leer hoe jy dit skryf. Dit vat omtrent twee minute.',
    calTipsTitle: 'Wenke',
    tipBig: 'Skryf groot: vul amper die hele blok.',
    tipOrder: 'Skryf antwoorde van links na regs.',
    tipFraction: "Gebruik ’n skuins streep / vir breuke, soos 3/4.",
    tipDot: "Maak die desimale komma ’n klein, duidelike merkie.",
    calStart: 'Begin',
    calNotNow: 'Nie nou nie',
    calSavedCount: 'Jy het {count} gestoorde handskrifvoorbeelde.',
    calReset: 'Vergeet my handskrif',
    calResetDone: 'Klaar! Dropstack gebruik weer sy gewone vorms.',
    calProgress: '{done} van {total}',
    calProgressLabel: 'Hoe ver jy is',
    calPrompt: 'Skryf {char}',
    calRepeat: '({n} van {total})',
    hintMinus: "’n Kort, plat streep (minus)",
    hintSlash: "’n Skuins streep, soos in 3/4",
    hintPoint: "’n Klein kolletjie of kommatjie",
    calPadLabel: 'Skryfblok vir {char}',
    calBack: 'Terug',
    calBackLabel: 'Gaan terug en skryf die vorige een weer',
    calRedo: 'Weer',
    calRedoLabel: 'Vee die blok skoon en skryf dit weer',
    calSkip: 'Slaan oor',
    calNext: 'Volgende',
    calSaveFinish: 'Stoor en klaar',
    calDoneTitle: 'Klaar!',
    calDoneBody: "Dropstack ken nou jou handskrif. Skryf ’n getal om dit te toets:",
    calTryLabel: "Toetsblok: skryf ’n getal",
    calTryResult: 'Ek lees: {text}',
    calTryEmpty: 'Skryf iets en ek sal dit lees.',
    calTryClear: 'Vee uit',
    calFinish: 'Klaar',
  },
};
